import type {AddFormV2Type} from 'context/guest';
import {FormFieldV2} from '@guestapp/sdk';
import i18n from 'i18next';
import {FormFieldsMapper} from '../types';
import {FORM_NAMES} from 'utils/guestForm';

type FieldGroup = {
  name: string;
  title: string;
  fields: FormFieldV2[];
  isCustom: boolean;
  order: number;
};

const getGroupTitle = (field: FormFieldV2) =>
  field.is_custom_group
    ? field.group_name
    : i18n.t(`default_form_group.${field.group_name}`);

const sortFieldsByOrder = (fields: FormFieldV2[]) =>
  fields.sort((a, b) => a.order - b.order);

const isActive = (field: FormFieldV2) => field.is_active;
const isOnlyForLeader = (isLeaderGuest: boolean) => (field: FormFieldV2) =>
  field.is_leaders_field ? field.is_required || isLeaderGuest : true;
const isDefaultInFieldsMapper =
  (formFieldsMapper: FormFieldsMapper) => (field: FormFieldV2) =>
    field.is_custom ? true : Boolean(formFieldsMapper[field.name as FORM_NAMES]);

const fieldsFilter =
  (formFieldsMapper: FormFieldsMapper, isLeaderGuest: boolean) =>
  (field: FormFieldV2) => {
    return (
      isActive(field) &&
      isOnlyForLeader(isLeaderGuest)(field) &&
      isDefaultInFieldsMapper(formFieldsMapper)(field)
    );
  };

export const groupByGroupName = (
  formFields: AddFormV2Type,
  formFieldsMapper: FormFieldsMapper,
  isLeaderGuest: boolean,
) => {
  const groups = sortFieldsByOrder(Object.values(formFields))
    .filter(fieldsFilter(formFieldsMapper, isLeaderGuest))
    .reduce((groupList, field) => {
      const group = groupList.find(({name}) => name === field.group_name);
      if (group) {
        group.fields.push(field);
      } else {
        groupList.push({
          name: field.group_name,
          title: getGroupTitle(field),
          fields: [field],
          isCustom: field.is_custom_group,
          order: field.group_order,
        });
      }

      return groupList;
    }, new Array<FieldGroup>());

  return groups.sort((a, b) => a.order - b.order);
};
